import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  EmployeeForSelectFragment,
  PerformanceReviewerStrategiesStrategyEnum,
} from 'generated/resources/graphql'
import { PerformanceReviewOverviewPageReviewProcessFragment } from 'generated/resources/graphql'
import pickBy from 'lodash/pickBy'

import { FilterOptions } from 'modules/core/components/Filter/filters/AddFilter'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { SupportedFilters } from './types'

interface FiltersContext {
  filters: SupportedFilters
  setFilter: <K extends keyof SupportedFilters>(key: K, value: SupportedFilters[K]) => void
  addVisibleFilter: (filterKey: string) => void
  newlyAddedFilters: string[]
  availableFilters: FilterOptions
  setAvailableFilters: (filters: FilterOptions) => void
}

const initialFilters: SupportedFilters = {
  managerId: null,
  employeeIds: [],
  onlyDirectReports: true,
  agreementStatus: null,
  evaluationsStatus: null,
  reviewerStrategy: null,
  scores: [],
  teamIds: [],
  tenureDates: [],
  workplaceIds: [],
  legalEntityIds: [],
  additionalVisibleFilters: [],
}

const FiltersContext = createContext<FiltersContext>({
  filters: initialFilters,
  setFilter: () => {},
  addVisibleFilter: () => {},
  newlyAddedFilters: [],
  availableFilters: [],
  setAvailableFilters: () => {},
})

export const useFiltersValues = () => {
  const { filters } = useContext(FiltersContext)

  return useMemo(
    () =>
      pickBy(filters, (value) => {
        if (Array.isArray(value) && value.length === 0) return false
        return value != null
      }),
    [filters]
  )
}

export const useFilter = <K extends keyof SupportedFilters>(filter: K) => {
  const { filters, setFilter } = useContext(FiltersContext)

  const setValue = useCallback(
    (value: SupportedFilters[K]) => setFilter(filter, value),
    [filter, setFilter]
  )

  return {
    value: filters[filter],
    setValue,
  }
}

export const useVisibleFilters = () => {
  const { filters, addVisibleFilter } = useContext(FiltersContext)
  return { visibleFilters: filters.additionalVisibleFilters, addVisibleFilter }
}

export const useNewlyAddedFilters = () => {
  const { newlyAddedFilters } = useContext(FiltersContext)
  return { newlyAddedFilters }
}

export const useAvailableFilters = () => {
  const { availableFilters, setAvailableFilters } = useContext(FiltersContext)
  return { availableFilters, setAvailableFilters }
}

export const isManagerFilterVisible = (
  reviewProcess: PerformanceReviewOverviewPageReviewProcessFragment
) => reviewProcess.targetManager.length > 1 || reviewProcess._policies?.fullVisibility.can

const defaultManager = ({
  initialValue,
  reviewProcess,
  currentEmployee,
}: {
  initialValue: string | 'none' | null
  reviewProcess: PerformanceReviewOverviewPageReviewProcessFragment
  currentEmployee?: EmployeeForSelectFragment
}) => {
  const isTargetManager = (managerId?: string): managerId is string => {
    if (!managerId) return false
    return reviewProcess.targetManager.some((manager) => String(manager.id) === managerId)
  }

  // "none" is used to identify when the user manually cleared the manager filter,
  // so we should keep the filter empty by not returning any employee.
  if (initialValue === 'none') return null

  if (isTargetManager(initialValue ?? undefined) && isManagerFilterVisible(reviewProcess)) {
    return initialValue
  }

  return isTargetManager(currentEmployee?.id) ? currentEmployee.id : null
}

const defaultReviewerStrategy = ({
  initialValue,
  reviewProcess,
}: {
  initialValue: PerformanceReviewerStrategiesStrategyEnum | null
  reviewProcess: PerformanceReviewOverviewPageReviewProcessFragment
}) => {
  return initialValue ?? reviewProcess.reviewerStrategies?.[0] ?? null
}

interface FiltersProviderProps {
  reviewProcess: PerformanceReviewOverviewPageReviewProcessFragment
  currentEmployee?: EmployeeForSelectFragment
  availableFilters?: FilterOptions
}

export const FiltersProvider = ({
  reviewProcess,
  currentEmployee,
  children,
  availableFilters = [],
}: PropsWithChildren<FiltersProviderProps>) => {
  const [storedFilters, setStoredFilters] = useLocalStorage<Record<string, SupportedFilters>>(
    LOCAL_STORAGE_KEYS.performanceProcessShowFilters
  )

  const storedFiltersRef = useRef(storedFilters)
  storedFiltersRef.current = storedFilters

  const setStoredFiltersRef = useRef(setStoredFilters)
  setStoredFiltersRef.current = setStoredFilters

  const [reviewProcessFilters, setReviewProcessFilters] = useState<SupportedFilters>(() => {
    const storedReviewProcessFilters = storedFilters?.[reviewProcess.id]
    const filters = storedReviewProcessFilters ?? initialFilters

    return {
      ...filters,
      scores: filters.scores ?? [],
      teamIds: filters.teamIds ?? [],
      tenureDates: filters.tenureDates ?? [],
      workplaceIds: filters.workplaceIds ?? [],
      legalEntityIds: filters.legalEntityIds ?? [],
      additionalVisibleFilters: filters.additionalVisibleFilters ?? [],
      managerId: defaultManager({
        initialValue:
          storedReviewProcessFilters && !storedReviewProcessFilters.managerId
            ? 'none'
            : filters.managerId,
        reviewProcess,
        currentEmployee,
      }),
      reviewerStrategy: defaultReviewerStrategy({
        initialValue: filters.reviewerStrategy,
        reviewProcess,
      }),
    }
  })

  const reviewProcessFiltersRef = useRef(reviewProcessFilters)
  reviewProcessFiltersRef.current = reviewProcessFilters

  const [newlyAddedFilters, setNewlyAddedFilters] = useState<string[]>([])
  const [availableFiltersState, setAvailableFiltersState] =
    useState<FilterOptions>(availableFilters)

  const setAvailableFilters = useCallback((filters: FilterOptions) => {
    setAvailableFiltersState(filters)
  }, [])

  const addVisibleFilter = useCallback(
    (filterKey: string) => {
      setReviewProcessFilters((prev) => {
        const newAdditionalVisibleFilters = prev.additionalVisibleFilters.includes(filterKey)
          ? prev.additionalVisibleFilters
          : [...prev.additionalVisibleFilters, filterKey]

        const newFilters = {
          ...prev,
          additionalVisibleFilters: newAdditionalVisibleFilters,
        }

        setStoredFiltersRef.current({
          ...storedFiltersRef.current,
          [reviewProcess.id]: newFilters,
        })

        return newFilters
      })

      // If the filter is not already visible, add it to the list of newly added filters
      if (!reviewProcessFiltersRef.current?.additionalVisibleFilters?.includes(filterKey)) {
        setNewlyAddedFilters((prev) => [...prev, filterKey])

        // Timeout to display the filter as "open" when just added, providing visual feedback
        setTimeout(() => {
          setNewlyAddedFilters((prev) => prev.filter((filter) => filter !== filterKey))
        }, 100)
      }
    },
    [reviewProcess.id]
  )

  const setFilter = useCallback(
    <K extends keyof SupportedFilters>(key: K, value: SupportedFilters[K]) => {
      setReviewProcessFilters((prev) => {
        const newFilters = { ...prev, [key]: value }
        setStoredFiltersRef.current({
          ...storedFiltersRef.current,
          [reviewProcess.id]: newFilters,
        })
        return newFilters
      })
    },
    [reviewProcess.id]
  )

  return (
    <FiltersContext.Provider
      value={{
        filters: reviewProcessFilters,
        setFilter,
        addVisibleFilter,
        newlyAddedFilters,
        availableFilters: availableFiltersState,
        setAvailableFilters: setAvailableFilters,
      }}
    >
      {children}
    </FiltersContext.Provider>
  )
}
